// src/app/core/interceptors/auth.interceptor.ts
import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Injectable, inject, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authReq: any;
  private static isSessionExpired = false;
  private translate = inject(TranslateService);
  private isBrowser: boolean;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    const errorPayload = err?.error?.payload;
    const errorCode = err?.error?.code;
    const errorMessage = err?.error?.message?.toLowerCase() || '';

    // Check if this is a SIGNUP/REGISTRATION endpoint
    const isSignupRequest = err.url?.includes('/signup') ||
      err.url?.includes('/register') ||
      err.url?.includes('SIGNUP');

    // Handle 401 / 403 / Token Expired
    const isTokenError = (
      errorMessage.includes('token expired') ||
      errorMessage.includes('invalid token') ||
      errorMessage.includes('token is expired') ||
      errorMessage.includes('jwt expired') ||
      errorMessage.includes('unauthorized access')
    );

    if (
      (err.status === 401 || errorCode === 'ERROR_CODE_UNAUTHORIZED') &&
      isTokenError &&
      !isSignupRequest
    ) {
      if (!AuthInterceptor.isSessionExpired) {
        AuthInterceptor.isSessionExpired = true;
        this.clearStorage();

        if (this.isBrowser) {
          alert(this.translate.instant('AUTH.SESSION_EXPIRED'));
        }

        setTimeout(() => {
          this.router.navigate(['/login']).then(() => {
            AuthInterceptor.isSessionExpired = false;
          });
        }, 1500);
      }

      return throwError(() => err);
    }

    // Handle 403 separately
    if (err.status === 403 && !isSignupRequest) {
      if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
        this.handleAccessDenied(errorPayload);
        return throwError(() => err);
      }

      if (isTokenError && !AuthInterceptor.isSessionExpired) {
        AuthInterceptor.isSessionExpired = true;
        this.clearStorage();

        if (this.isBrowser) {
          alert(this.translate.instant('AUTH.SESSION_EXPIRED'));
        }

        setTimeout(() => {
          this.router.navigate(['/login']).then(() => {
            AuthInterceptor.isSessionExpired = false;
          });
        }, 1500);

        return throwError(() => err);
      }
    }

    // Onboarding-related redirects
    if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
      this.handleAccessDenied(errorPayload);
      return throwError(() => err);
    }

    // Other errors
    if (!isSignupRequest) {
      console.error('API Error:', err?.error?.message || err?.message || 'Something went wrong');
    }

    return throwError(() => err);
  }

  private handleAccessDenied(errorPayload: any): void {
    if (errorPayload) {
      if (errorPayload.company_status === "user don't have a registered company") {
        if (this.isBrowser) alert(this.translate.instant('AUTH.COMPLETE_COMPANY_SETUP'));
        setTimeout(() => this.router.navigate(['/setup-company']), 2000);
      } else if (errorPayload.subscription_status === 'user in not subscribed yet') {
        if (this.isBrowser) alert(this.translate.instant('AUTH.SUBSCRIBE_TO_PLAN'));
        setTimeout(() => this.router.navigate(['/choose-plan']), 2000);
      } else if (errorPayload.verification_status === 'user need to verify there account using top') {
        if (this.isBrowser) alert(this.translate.instant('AUTH.VERIFY_OTP'));
        setTimeout(() => this.router.navigate(['/verify-otp']), 2000);
      } else {
        if (this.isBrowser) alert(this.translate.instant('AUTH.ACCESS_DENIED'));
        setTimeout(() => this.router.navigate(['/login']), 2000);
      }
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = this.isBrowser ? (localStorage.getItem('jwtToken') || '') : '';

    this.authReq = req.clone({
      setHeaders: {
        Authorization: token ? `Bearer ${token}` : '',
        'X-Data-Source': environment.dataSource,
      },
    });

    return next.handle(this.authReq).pipe(
      catchError((x) => this.handleAuthError(x))
    );
  }

  clearStorage() {
    if (this.isBrowser) {
      localStorage.clear();
      sessionStorage.clear();
    }
  }
}
